import { forwardRef, useState } from 'react';
import { css, Global } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import Modal, { ModalRef } from '@/components/@IntranetLibrary/Modal';
import LoadingLottie from '@/components/LoadingLottie';
import DocumentIcon from '@/public/icons/approval/document.svg';
import ReconnectArrowIcon from '@/public/icons/approval/reconnect-arrow.svg';
import useRelationHistory from '@/hooks/approval/useRelationHistory';
import useApprovalView from '@/hooks/approval/useApprovalView';
import useRelatedApprovalMutations from '@/hooks/approval/useRelatedApprovalMutations';
import { useSnackbar } from '@/hooks/global-tools';
import { getApprovalDocUrl } from '@/utils/urls';
import { formatApprovalDateTime } from '@/utils/approval/formatApprovalDate';
import RelatedItemRow from '@/components/Approval/RelatedApproval/RelatedItemRow';
import type {
  RelatedApprovalRelationType,
  RelationHistoryAction,
  RelationHistoryBatch,
  RelationHistoryItem,
} from '@/types/approval';
import {
  HistoryBatchHeader,
  HistoryBatchMessage,
  HistoryBatchRoot,
  HistoryBatchTime,
  HistoryFallback,
  HistoryItemIcon,
  HistoryListRoot,
  ReconnectArrow,
  ReconnectChipButton,
  ReconnectRow,
} from './styles';

/**
 * 연관품의 변경이력 모달 — 열려있을 때만 GET /api/v1/history/approval/relation/{docid},
 * updatedAt desc + 1초 미만 인접 항목 배치 그룹핑. "다시 연결"=POST /relations 단건,
 * cascade_delete / 대상 품의 삭제(D)는 "다시 연결" 미노출.
 * TODO: 편집 모드 변경분 저장 시 본 모달도 SWR mutate 필요.
 */

type RelatedHistoryModalProps = {
  documentId: string | null;
};

// 변경이력 행은 딱지 뒤 작성자명이 없어 tag margin-right 0 으로 우측 정렬, 본문 패딩만 PC/모바일 분기.
// 이 모달 scope 한정 — 공용 기본값/다른 화면(검색·선택·조회)은 불변.
const historyModalMobilePadding = css`
  .history-modal .modal-body {
    padding: 24px 20px;
  }
  .history-modal .approval-status-tag {
    margin-right: 0;
  }

  @media (min-width: 576px) {
    .history-modal .modal-body {
      padding: 32px 24px;
    }
  }
`;

const ACTION_MESSAGE_KEY: Record<RelationHistoryAction, string> = {
  create: '{{name}}님이 연결 품의를 추가했습니다.',
  delete: '{{name}}님이 연결 품의를 삭제했습니다.',
  cascade_delete: '품의가 삭제되어 연결 품의에서 제외되었습니다.',
};

const RelatedHistoryModal = forwardRef<ModalRef, RelatedHistoryModalProps>(
  ({ documentId }, ref) => {
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    // 열려있을 때만 fetch — 모달 open 여부는 ref 내부 state 라 open/close 콜백으로 별도 추적.
    const [shouldFetch, setShouldFetch] = useState(false);

    const { batches, isLoading } = useRelationHistory(
      documentId,
      shouldFetch,
    );

    // 연관품의 카드 리스트는 v2 단건조회에서 파생 — 다시 연결 성공 시 v2 캐시 재검증.
    const { mutate: mutateRelations } = useApprovalView(documentId);
    const { addRelations } = useRelatedApprovalMutations();

    const [reconnectingItemId, setReconnectingItemId] = useState<string | null>(
      null,
    );

    const handleReconnect = async (
      item: RelationHistoryItem,
      itemKey: string,
    ) => {
      if (!documentId) return;
      const relatedDocId = item.relatedDocument?.docId ?? item.relatedDocumentId;
      const relationType: RelatedApprovalRelationType | undefined =
        item.relationType;
      if (!relatedDocId || !relationType) {
        snackbar.showError({
          message: t('다시 연결에 실패했습니다.'),
        });
        return;
      }
      try {
        setReconnectingItemId(itemKey);
        await addRelations(documentId, [
          { id: relatedDocId, relationType },
        ]);
        snackbar.showSuccess({ message: t('다시 연결에 성공했습니다.') });
        await mutateRelations();
      } catch {
        snackbar.showError({ message: t('다시 연결에 실패했습니다.') });
      } finally {
        setReconnectingItemId(null);
      }
    };

    const renderBatch = (batch: RelationHistoryBatch) => {
      const actorName = batch.actor?.name ?? '';
      const baseMessage = ACTION_MESSAGE_KEY[batch.action];
      const message =
        batch.action === 'cascade_delete'
          ? t(baseMessage)
          : t(baseMessage, { name: actorName });

      const isDeleted =
        batch.action === 'delete' || batch.action === 'cascade_delete';

      return (
        <HistoryBatchRoot key={batch.id}>
          <HistoryBatchHeader>
            <HistoryBatchMessage>{message}</HistoryBatchMessage>
            <HistoryBatchTime>{formatApprovalDateTime(batch.updatedAt)}</HistoryBatchTime>
          </HistoryBatchHeader>
          {batch.items.map((item, idx) => {
            const docId = item.relatedDocument?.docId ?? item.relatedDocumentId;
            const subject = item.relatedDocument?.subject ?? '';
            const itemKey = item.id ?? `${batch.id}-${idx}`;
            const isTargetDeleted = item.relatedDocument?.status === 'D';
            const showReconnect =
              batch.action === 'delete' && !isTargetDeleted && !!docId;
            return (
              <RelatedItemRow
                key={itemKey}
                layout="inline"
                titleTone="history"
                strikethrough={isDeleted}
                leading={
                  <HistoryItemIcon>
                    <DocumentIcon />
                  </HistoryItemIcon>
                }
                title={subject}
                href={docId ? getApprovalDocUrl(docId) : undefined}
                status={item.relatedDocument?.status}
                below={
                  showReconnect ? (
                    <ReconnectRow>
                      <ReconnectArrow>
                        <ReconnectArrowIcon width={14} height={14} aria-hidden />
                      </ReconnectArrow>
                      <ReconnectChipButton
                        type="button"
                        onClick={() => handleReconnect(item, itemKey)}
                        disabled={reconnectingItemId === itemKey}
                      >
                        {t('다시 연결')}
                      </ReconnectChipButton>
                    </ReconnectRow>
                  ) : undefined
                }
              />
            );
          })}
        </HistoryBatchRoot>
      );
    };

    return (
      <>
        <Global styles={historyModalMobilePadding} />
        <Modal
          ref={ref}
          title={t('연결 품의 변경이력')}
          size="small"
          className="history-modal"
          onOpen={() => setShouldFetch(true)}
          handleClose={() => setShouldFetch(false)}
          // 모바일 풀폭 scope — 공유 Modal 전역 폭은 건드리지 않고 이 모달의 .modal-inner.small 만 override.
          sx={{
            '& .modal-inner.small': {
              '@media (max-width: 575.98px)': {
                width: 'calc(100vw - 32px)',
                maxWidth: 'calc(100vw - 32px)',
              },
            },
          }}
        >
          {/* fetch 중 + 데이터 없음 → spinner. fetch 후 비어있음 → 안내 텍스트. */}
          {isLoading && batches.length === 0 ? (
            <HistoryFallback>
              <LoadingLottie opacity={1} loop />
            </HistoryFallback>
          ) : batches.length === 0 ? (
            <HistoryFallback>{t('변경 이력이 없습니다.')}</HistoryFallback>
          ) : (
            <HistoryListRoot>{batches.map(renderBatch)}</HistoryListRoot>
          )}
        </Modal>
      </>
    );
  },
);
RelatedHistoryModal.displayName = 'RelatedHistoryModal';

export default RelatedHistoryModal;
